import axios from 'axios';
import { axiosClientV2 } from './axios.client.js';
import { getApiV2BaseUrl } from '../config/brand';

const emptyFlow = (id, name) => ({
  schemaVersion: 'flow/v2',
  id,
  name,
  nodes: [
    { id: 'start', type: 'start', position: { x: 100, y: 100 } },
    { id: 'end', type: 'end', position: { x: 320, y: 100 } },
  ],
  edges: [
    {
      id: 'e1', source: 'start', target: 'end', kind: 'control',
    },
  ],
});

const emptyApp = (id, name) => ({
  schemaVersion: 'app/v2',
  id,
  name,
  layout: 'three-panel',
  chatPosition: 'center',
  panels: [
    {
      id: 'center_chat',
      type: 'chat',
      position: 'center',
      order: 0,
      fixed: true,
    },
  ],
});

export const studioV2NewFlowDraft = (name = 'Untitled flow') => {
  const id = typeof crypto !== 'undefined' && crypto.randomUUID
    ? `flow_${crypto.randomUUID()}`
    : `flow_${Date.now()}`;
  return emptyFlow(id, name);
};

export const studioV2NewAppDraft = (name = 'Untitled app') => {
  const id = typeof crypto !== 'undefined' && crypto.randomUUID
    ? `app_${crypto.randomUUID()}`
    : `app_${Date.now()}`;
  return emptyApp(id, name);
};

export const listV2Flows = async () => {
  try {
    const data = await axiosClientV2.get('flows');
    const flows = data?.flows ?? [];
    return { flows, error: null };
  } catch (e) {
    return { flows: [], error: e?.message || 'Failed to list flows' };
  }
};

/** Which AI model providers the flow runtime can use right now (no key material). */
export const getV2AiStatus = async () => {
  try {
    const data = await axiosClientV2.get('ai/status');
    return { status: data?.status ?? null, error: null };
  } catch (e) {
    return { status: null, error: e?.message || 'Failed to load AI model status' };
  }
};

export const getV2FlowDiagnosticsCounts = async () => {
  try {
    const data = await axiosClientV2.get('flows/diagnostics/counts');
    return {
      ownedCount: Number(data?.ownedCount || 0),
      tenantCount: Number(data?.tenantCount || 0),
      error: null,
    };
  } catch (e) {
    return { ownedCount: 0, tenantCount: 0, error: e?.message || 'Failed to load flow diagnostics' };
  }
};

export const getV2Flow = async (mongoId) => {
  try {
    const data = await axiosClientV2.get(`flows/${mongoId}`);
    return { flow: data?.flow ?? null, error: null };
  } catch (e) {
    return { flow: null, error: e?.message || 'Failed to load flow' };
  }
};

/** Multi-turn AI chat to edit the flow (returns optional validated proposedDefinition). */
export const sendV2FlowAiChat = async (mongoId, { messages, model, clientDiagnostics }) => {
  try {
    const body = { messages, model };
    if (clientDiagnostics != null) body.clientDiagnostics = clientDiagnostics;
    const data = await axiosClientV2.post(`flows/${mongoId}/ai-chat`, body);
    return {
      reply: data?.reply ?? '',
      proposedDefinition: data?.proposedDefinition ?? null,
      proposedValidation: data?.proposedValidation ?? null,
      graphAnalysis: data?.graphAnalysis ?? null,
      model: data?.model ?? null,
      error: null,
    };
  } catch (e) {
    return {
      reply: '',
      proposedDefinition: null,
      proposedValidation: null,
      graphAnalysis: null,
      model: null,
      error: typeof e === 'string' ? e : (e?.message || e?.error || 'AI chat failed'),
    };
  }
};

/** Streaming AI chat for flow editor via SSE events. */
export const sendV2FlowAiChatStream = async (
  mongoId,
  { messages, model, clientDiagnostics, signal, onEvent },
) => {
  try {
    const body = { messages, model };
    if (clientDiagnostics != null) body.clientDiagnostics = clientDiagnostics;

    const token = localStorage.getItem('tkn');
    const base = String(getApiV2BaseUrl() || '/api/v2').replace(/\/$/, '');
    const url = `${base}/flows/${encodeURIComponent(String(mongoId))}/ai-chat/stream`;
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      signal,
    });
    if (!res.ok || !res.body) {
      return {
        reply: '',
        proposedDefinition: null,
        proposedValidation: null,
        graphAnalysis: null,
        model: null,
        error: `Stream failed (${res.status})`,
      };
    }

    const decoder = new TextDecoder();
    const reader = res.body.getReader();
    let buffer = '';
    let final = {
      reply: '',
      proposedDefinition: null,
      proposedValidation: null,
      graphAnalysis: null,
      model: null,
      error: null,
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() || '';
      for (const raw of chunks) {
        const lines = raw.split('\n').map((l) => l.trim()).filter(Boolean);
        const dataLine = lines.find((l) => l.startsWith('data:'));
        if (!dataLine) continue;
        let payload;
        try {
          payload = JSON.parse(dataLine.replace(/^data:\s*/, ''));
        } catch (_) {
          continue;
        }
        onEvent?.(payload);
        if (payload?.type === 'done') {
          final = {
            reply: payload.reply ?? '',
            proposedDefinition: payload.proposedDefinition ?? null,
            proposedValidation: payload.proposedValidation ?? null,
            graphAnalysis: payload.graphAnalysis ?? null,
            model: payload.model ?? null,
            fallback: payload.fallback ?? null,
            error: payload.error || null,
          };
        }
        if (payload?.type === 'error') {
          final.error = payload.error || 'AI stream failed';
        }
      }
    }
    return final;
  } catch (e) {
    return {
      reply: '',
      proposedDefinition: null,
      proposedValidation: null,
      graphAnalysis: null,
      model: null,
      error: typeof e === 'string' ? e : (e?.message || 'AI stream failed'),
    };
  }
};

/** Streaming AI chat for app editor via SSE events. */
export const sendV2AppAiChatStream = async (
  mongoId,
  { messages, model, clientDiagnostics, editorDefinition, signal, onEvent },
) => {
  try {
    const body = { messages, model };
    if (clientDiagnostics != null) body.clientDiagnostics = clientDiagnostics;
    if (editorDefinition != null && typeof editorDefinition === 'object') {
      body.editorDefinition = editorDefinition;
    }

    const token = localStorage.getItem('tkn');
    const base = String(getApiV2BaseUrl() || '/api/v2').replace(/\/$/, '');
    const url = `${base}/apps/${encodeURIComponent(String(mongoId))}/ai-chat/stream`;
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      signal,
    });
    if (!res.ok || !res.body) {
      return {
        reply: '',
        proposedDefinition: null,
        proposedValidation: null,
        appAnalysis: null,
        model: null,
        error: `Stream failed (${res.status})`,
      };
    }

    const decoder = new TextDecoder();
    const reader = res.body.getReader();
    let buffer = '';
    let final = {
      reply: '',
      proposedDefinition: null,
      proposedValidation: null,
      appAnalysis: null,
      model: null,
      error: null,
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() || '';
      for (const raw of chunks) {
        const lines = raw.split('\n').map((l) => l.trim()).filter(Boolean);
        const dataLine = lines.find((l) => l.startsWith('data:'));
        if (!dataLine) continue;
        let payload;
        try {
          payload = JSON.parse(dataLine.replace(/^data:\s*/, ''));
        } catch (_) {
          continue;
        }
        onEvent?.(payload);
        if (payload?.type === 'done') {
          final = {
            reply: payload.reply ?? '',
            proposedDefinition: payload.proposedDefinition ?? null,
            proposedValidation: payload.proposedValidation ?? null,
            appAnalysis: payload.appAnalysis ?? null,
            model: payload.model ?? null,
            fallback: payload.fallback ?? null,
            error: payload.error || null,
          };
        }
        if (payload?.type === 'error') {
          final.error = payload.error || 'AI stream failed';
        }
      }
    }
    return final;
  } catch (e) {
    return {
      reply: '',
      proposedDefinition: null,
      proposedValidation: null,
      appAnalysis: null,
      model: null,
      error: typeof e === 'string' ? e : (e?.message || 'AI stream failed'),
    };
  }
};

/** Multi-turn AI chat to edit a V2 app (returns optional validated proposedDefinition). */
export const sendV2AppAiChat = async (mongoId, { messages, model, clientDiagnostics, editorDefinition }) => {
  try {
    const body = { messages, model };
    if (clientDiagnostics != null) body.clientDiagnostics = clientDiagnostics;
    if (editorDefinition != null && typeof editorDefinition === 'object') {
      body.editorDefinition = editorDefinition;
    }
    const data = await axiosClientV2.post(`apps/${mongoId}/ai-chat`, body);
    return {
      reply: data?.reply ?? '',
      proposedDefinition: data?.proposedDefinition ?? null,
      proposedValidation: data?.proposedValidation ?? null,
      appAnalysis: data?.appAnalysis ?? null,
      model: data?.model ?? null,
      error: null,
    };
  } catch (e) {
    return {
      reply: '',
      proposedDefinition: null,
      proposedValidation: null,
      appAnalysis: null,
      model: null,
      error: typeof e === 'string' ? e : (e?.message || e?.error || 'AI chat failed'),
    };
  }
};

/** Full system prompt for external LLM / MCP workflow authoring (server reads builder-schema). */
export const getV2WorkflowBuilderPrompt = async (mongoId) => {
  try {
    const id = encodeURIComponent(String(mongoId || '').trim());
    const data = await axiosClientV2.get(`flows/${id}/workflow-builder-prompt`);
    return { prompt: data?.prompt ?? '', error: null };
  } catch (e) {
    const msg = typeof e === 'string'
      ? e
      : (e?.message || e?.error || e?.detail || JSON.stringify(e) || 'Failed to load workflow builder prompt');
    return { prompt: '', error: msg };
  }
};

export const createV2Flow = async (definition) => {
  try {
    const data = await axiosClientV2.post('flows', definition);
    return { flow: data?.flow ?? null, error: null };
  } catch (e) {
    return { flow: null, error: e?.message || String(e) };
  }
};

export const updateV2Flow = async (mongoId, definition) => {
  try {
    const data = await axiosClientV2.put(`flows/${mongoId}`, definition);
    return { flow: data?.flow ?? null, error: null };
  } catch (e) {
    return { flow: null, error: e?.message || String(e) };
  }
};

export const deleteV2Flow = async (mongoId) => {
  try {
    await axiosClientV2.delete(`flows/${mongoId}`);
    return { error: null };
  } catch (e) {
    return { error: e?.message || String(e) };
  }
};

/** Run V2 flow via Python /execute-flow (async job — returns execution_id when supported). */
export const executeV2Flow = async (mongoId, input = { input: 'Hello' }, execOpts = {}) => {
  try {
    const body = { flowId: mongoId, input };
    if (execOpts.debugProfileId) body.debugProfileId = execOpts.debugProfileId;
    if (Array.isArray(execOpts.assetIds) && execOpts.assetIds.length) body.assetIds = execOpts.assetIds;
    const data = await axiosClientV2.post('executions/run', body);
    return { result: data, error: null };
  } catch (e) {
    return { result: null, error: e?.message || String(e) };
  }
};

/** Step-through debug: pause before each node (or at breakpoints) and use Debug tab Step / F10. */
export const executeV2FlowDebug = async (mongoId, input = { input: 'Hello' }, execOpts = {}) => {
  try {
    const body = {
      flowId: mongoId,
      input,
      pauseOnEveryNode: execOpts.pauseOnEveryNode !== false,
      breakpoints: Array.isArray(execOpts.breakpoints) ? execOpts.breakpoints : [],
    };
    if (execOpts.debugProfileId) body.debugProfileId = execOpts.debugProfileId;
    if (Array.isArray(execOpts.assetIds) && execOpts.assetIds.length) body.assetIds = execOpts.assetIds;
    const data = await axiosClientV2.post('executions/debug/run', body);
    return { result: data, error: null };
  } catch (e) {
    return { result: null, error: e?.message || String(e) };
  }
};

export const listV2Apps = async () => {
  try {
    const data = await axiosClientV2.get('apps');
    const apps = data?.apps ?? [];
    return { apps, error: null };
  } catch (e) {
    return { apps: [], error: e?.message || 'Failed to list apps' };
  }
};

export const getV2App = async (mongoId) => {
  try {
    const data = await axiosClientV2.get(`apps/${mongoId}`);
    return { app: data?.app ?? null, error: null };
  } catch (e) {
    return { app: null, error: e?.message || 'Failed to load app' };
  }
};

export const createV2App = async (definition) => {
  try {
    const data = await axiosClientV2.post('apps', definition);
    return { app: data?.app ?? null, error: null };
  } catch (e) {
    return { app: null, error: e?.message || String(e) };
  }
};

export const updateV2App = async (mongoId, definition) => {
  try {
    const data = await axiosClientV2.put(`apps/${mongoId}`, definition);
    return { app: data?.app ?? null, error: null };
  } catch (e) {
    return { app: null, error: e?.message || String(e) };
  }
};

export const deleteV2App = async (mongoId) => {
  try {
    await axiosClientV2.delete(`apps/${mongoId}`);
    return { error: null };
  } catch (e) {
    return { error: e?.message || String(e) };
  }
};

/** PATCH lifecycle only (e.g. publish) — body is not the app JSON schema. */
export const patchV2AppMeta = async (mongoId, { status }) => {
  try {
    const data = await axiosClientV2.patch(`apps/${mongoId}`, { status });
    return { app: data?.app ?? null, error: null };
  } catch (e) {
    return { app: null, error: e?.message || String(e) };
  }
};

export const listV2AppShares = async (mongoId) => {
  try {
    const data = await axiosClientV2.get(`apps/${mongoId}/shares`);
    return { shares: data?.shares ?? [], error: null };
  } catch (e) {
    return { shares: [], error: e?.message || String(e) };
  }
};

export const addV2AppShare = async (mongoId, { targetUserId, role }) => {
  try {
    const data = await axiosClientV2.post(`apps/${mongoId}/shares`, { targetUserId, role });
    return { share: data?.share ?? null, error: null };
  } catch (e) {
    return { share: null, error: e?.message || String(e) };
  }
};

export const removeV2AppShare = async (mongoId, shareUserId) => {
  try {
    await axiosClientV2.delete(`apps/${mongoId}/shares/${shareUserId}`);
    return { error: null };
  } catch (e) {
    return { error: e?.message || String(e) };
  }
};

export const listV2AppRevisions = async (mongoId) => {
  try {
    const data = await axiosClientV2.get(`apps/${mongoId}/revisions`);
    return {
      revisions: data?.revisions ?? [],
      accessRole: data?.accessRole ?? null,
      publishedRevisionId: data?.publishedRevisionId ?? null,
      error: null,
    };
  } catch (e) {
    return { revisions: [], accessRole: null, publishedRevisionId: null, error: e?.message || String(e) };
  }
};

export const listV2AppRuntimeEvents = async (mongoId, opts = {}) => {
  try {
    const params = new URLSearchParams();
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.panelId) params.set('panelId', String(opts.panelId));
    const qs = params.toString();
    const data = await axiosClientV2.get(`apps/${mongoId}/runtime-events${qs ? `?${qs}` : ''}`);
    return {
      events: data?.events ?? [],
      accessRole: data?.accessRole ?? null,
      error: null,
    };
  } catch (e) {
    return { events: [], accessRole: null, error: e?.message || String(e) };
  }
};

export const createV2AppRuntimeEvent = async (mongoId, event) => {
  try {
    const data = await axiosClientV2.post(`apps/${mongoId}/runtime-events`, event);
    return { event: data?.event ?? null, error: null };
  } catch (e) {
    return { event: null, error: e?.message || String(e) };
  }
};

export const createV2AppRevision = async (mongoId, payload = {}) => {
  try {
    const data = await axiosClientV2.post(`apps/${mongoId}/revisions`, payload);
    return { revision: data?.revision ?? null, error: null };
  } catch (e) {
    return { revision: null, error: e?.message || String(e) };
  }
};

export const restoreV2AppRevision = async (mongoId, revisionId) => {
  try {
    const data = await axiosClientV2.post(`apps/${mongoId}/revisions/${revisionId}/restore`, {});
    return { app: data?.app ?? null, error: null };
  } catch (e) {
    return { app: null, error: e?.message || String(e) };
  }
};

/** Public published app — no auth (CORS must allow the Studio host). */
export const fetchPublicV2App = async (mongoId) => {
  const base = `${getApiV2BaseUrl().replace(/\/?$/, '')}/`;
  try {
    const res = await fetch(`${base}apps/public/${mongoId}`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { app: null, error: data?.message || res.statusText || 'Failed to load app' };
    }
    return { app: data?.app ?? null, error: null };
  } catch (e) {
    return { app: null, error: e?.message || 'Failed to load app' };
  }
};

export const getV2InterviewAppSession = async (mongoId) => {
  try {
    const data = await axiosClientV2.get(`apps/${mongoId}/interview-session`);
    return { data, error: null };
  } catch (e) {
    return { data: null, error: e?.message || 'Failed to load app session' };
  }
};

export const putV2InterviewAppSession = async (mongoId, body) => {
  try {
    const data = await axiosClientV2.put(`apps/${mongoId}/interview-session`, body);
    return { data, error: null };
  } catch (e) {
    return { data: null, error: e?.message || 'Failed to save app session' };
  }
};

async function postV2Sse(path, body, onEvent, signal) {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('tkn') : null;
  const base = `${getApiV2BaseUrl().replace(/\/?$/, '')}/`;
  const res = await fetch(`${base}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'X-UI-Version': 'v2',
    },
    credentials: 'include',
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    throw new Error(errBody.message || errBody.error || res.statusText);
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split('\n\n');
    buf = parts.pop() || '';
    for (const block of parts) {
      const lines = block.split('\n').filter((l) => l.startsWith('data:'));
      for (const line of lines) {
        const json = line.replace(/^data:\s*/i, '').trim();
        if (!json) continue;
        try {
          onEvent(JSON.parse(json));
        } catch (_) {
          /* ignore non-JSON line */
        }
      }
    }
  }
}

/**
 * Stream V2 flow execution (SSE). Calls onEvent for each parsed `data: {...}` payload.
 * A run that stops at a human-input step ends with an `approval_required` event.
 */
export const executeV2FlowStream = async (mongoId, input, onEvent, opts = {}) => {
  const { signal, debugProfileId, assetIds } = opts;
  const body = { flowId: mongoId, input };
  if (debugProfileId) body.debugProfileId = debugProfileId;
  if (Array.isArray(assetIds) && assetIds.length) body.assetIds = assetIds;
  return postV2Sse('executions/stream', body, onEvent, signal);
};

/** Approves, rejects or replies to a paused run and streams the rest of it. */
export const decideV2ApprovalStream = (approvalId, decision, onEvent, { signal } = {}) => (
  postV2Sse(`approvals/${encodeURIComponent(approvalId)}/decide`, decision, onEvent, signal)
);

export const listV2Approvals = async ({ flowId, status = 'pending' } = {}) => {
  try {
    const data = await axiosClientV2.get('approvals', { params: { status, ...(flowId ? { flowId } : {}) } });
    return { approvals: data?.approvals || [], error: null };
  } catch (e) {
    return { approvals: [], error: e?.message || 'Could not load approvals' };
  }
};

/**
 * Lists tenant V2 assets. Older deployments may not expose GET /api/v2/assets yet — treat 404 as empty list
 * so Studio does not surface a hard error or noisy axios rejection for InterviewFlow / staged backends.
 */
export const listV2Assets = async () => {
  try {
    const base = String(axiosClientV2.defaults.baseURL || '/api/v2/').replace(/\/?$/, '/');
    const url = `${base}assets`;
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('tkn') : null;
    const res = await axios.get(url, {
      withCredentials: true,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'X-UI-Version': 'v2',
      },
      validateStatus: (status) => status === 200 || status === 404,
    });
    if (res.status === 404) {
      return { assets: [], error: null };
    }
    const body = res.data || {};
    return { assets: body.assets ?? [], error: null };
  } catch (e) {
    const msg = e?.response?.data?.message || e?.message || String(e);
    return { assets: [], error: msg };
  }
};

export const uploadV2Asset = async (file, body = {}) => {
  try {
    const form = new FormData();
    form.append('file', file);
    if (body.kind) form.append('kind', body.kind);
    const data = await axiosClientV2.post('assets', form);
    return { asset: data?.asset ?? null, error: null };
  } catch (e) {
    return { asset: null, error: e?.message || String(e) };
  }
};

export const getV2Asset = async (assetId) => {
  try {
    const data = await axiosClientV2.get(`assets/${assetId}`);
    return { asset: data?.asset ?? null, error: null };
  } catch (e) {
    return { asset: null, error: e?.message || String(e) };
  }
};

export const queueV2AssetProcessing = async (assetId) => {
  try {
    const data = await axiosClientV2.post(`assets/${assetId}/process`, {});
    return { asset: data?.asset ?? null, error: null };
  } catch (e) {
    return { asset: null, error: e?.message || String(e) };
  }
};

export const listV2DebugProfiles = async () => {
  try {
    const data = await axiosClientV2.get('debug/profiles');
    return { profiles: data?.profiles ?? [], error: null };
  } catch (e) {
    return { profiles: [], error: e?.message || String(e) };
  }
};

export const createV2DebugProfile = async (name, payload = {}) => {
  try {
    const data = await axiosClientV2.post('debug/profiles', { name: name.trim(), payload });
    return { profile: data?.profile ?? null, error: null };
  } catch (e) {
    return { profile: null, error: e?.message || String(e) };
  }
};

export const deleteV2DebugProfile = async (id) => {
  try {
    await axiosClientV2.delete(`debug/profiles/${id}`);
    return { error: null };
  } catch (e) {
    return { error: e?.message || String(e) };
  }
};
